/**
 * SocialPulseHome — the new network-hub homepage at socialpulse.com.
 *
 * Design brief (approved by owner, Feb 2026):
 *   - Clean, editorial, "one identity → many pulses" positioning
 *   - Self-contained (renders its own header + footer; App.js suppresses
 *     the legacy Header + TaglineBanner + LocationHeroStrip when this
 *     page is active)
 *   - Hero: bold navy headline, dual place/topic selector, illustrated
 *     world-map collage right-side
 *   - Tab bar (For You / Near You / Following / Latest / Causes)
 *   - "Your Local Pulse" 4-card grid tied to detected locality
 *   - "Explore the SocialPulse Network" tile row linking to sister sites
 */
import { Suspense, useEffect } from "react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import HeroCollage from "@/components/socialpulse/HeroCollage";
import HeroExploreForm from "@/components/socialpulse/HeroExploreForm";
import PulseTabs from "@/components/socialpulse/PulseTabs";
import LocalPulseGrid from "@/components/socialpulse/LocalPulseGrid";
import NetworkRow from "@/components/socialpulse/NetworkRow";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";
import { SocialPulseProfileBanner } from "@/pages/SocialPulseProfileOnboard";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function SocialPulseHome() {
  useEffect(() => {
    // Phase 0 audit fix — unique title, description, canonical, OG cards.
    document.title = "SocialPulse — Discover Your Community. Connect With the World.";
    setMeta("description", "SocialPulse is the parent network for community, civic, editorial, travel, alumni and specialist sites — one identity, many pulses. Explore local stories, help, events and resources.");
    setLink("canonical", "https://socialpulse.com/");
    setMeta("og:title",        "SocialPulse — Discover Your Community. Connect With the World.", "property");
    setMeta("og:description",  "One connected network for communities, places, people, help, knowledge and positive action.", "property");
    setMeta("og:type",         "website", "property");
    setMeta("og:url",          "https://socialpulse.com/", "property");
    setMeta("twitter:card",    "summary_large_image");
    setMeta("twitter:title",   "SocialPulse — One network. Many pulses.");
    setMeta("twitter:description", "Explore local stories, find or offer help, join conversations and build Social Capital across the SocialPulse network.");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="socialpulse-home">
      <SocialPulseHeader />

      {/* Signed-in profile banner — dismissible */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 pt-4">
        <SocialPulseProfileBanner />
      </div>

      {/* ────────── Hero ────────── */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #F0F6FF 0%, #E7F0FA 55%, #F5F9FE 100%)",
        }}
        data-testid="sp-hero"
      >
        {/* Ambient gradient orbs — sit BEHIND the hero content, no dark surfaces */}
        <div
          aria-hidden
          className="absolute top-[-6rem] right-[-6rem] w-[38rem] h-[38rem] rounded-full blur-[110px] opacity-60 pointer-events-none"
          style={{ background: "radial-gradient(circle, #A7F3D0 0%, #6EE7B7 40%, transparent 72%)" }}
        />
        <div
          aria-hidden
          className="absolute top-24 right-32 w-[30rem] h-[30rem] rounded-full blur-[110px] opacity-55 pointer-events-none"
          style={{ background: "radial-gradient(circle, #DDD6FE 0%, #C4B5FD 45%, transparent 78%)" }}
        />
        <div
          aria-hidden
          className="absolute bottom-[-4rem] left-[-4rem] w-[26rem] h-[26rem] rounded-full blur-[110px] opacity-45 pointer-events-none"
          style={{ background: "radial-gradient(circle, #FED7AA 0%, #FDBA74 55%, transparent 82%)" }}
        />
        <div
          aria-hidden
          className="absolute top-1/3 left-1/3 w-[22rem] h-[22rem] rounded-full blur-[110px] opacity-35 pointer-events-none"
          style={{ background: "radial-gradient(circle, #FBCFE8 0%, #F9A8D4 55%, transparent 82%)" }}
        />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 pt-8 sm:pt-10 lg:pt-14 pb-10 lg:pb-16 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-8 sm:gap-10 items-center">
          <div className="relative">
            <h1
              className="font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem]"
              data-testid="sp-hero-headline"
            >
              Discover Your Community.
              <br />
              Connect With the World.
            </h1>

            {/* Handwritten accent (right of headline on wide screens) */}
            <div
              className="hidden xl:block absolute -right-6 top-16 rotate-[-6deg] pointer-events-none select-none"
              data-testid="sp-handwrite-people"
            >
              <p className="font-serif italic text-blue-700 text-[15px] leading-tight">
                <span className="block">People</span>
                <span className="block ml-4">Places</span>
                <span className="block ml-8">Possibilities</span>
              </p>
              <svg width="120" height="14" className="ml-2 -mt-1">
                <path d="M4 8 Q 40 2, 116 8" stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </div>

            <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl">
              Explore local places, helpful people, community stories,
              businesses, causes and opportunities throughout the SocialPulse
              network.
            </p>
            <div className="mt-6 sm:mt-7">
              <HeroExploreForm />
            </div>
            <div className="mt-5 flex flex-wrap gap-x-2 gap-y-1 text-sm">
              <a href="/blog/write" className="text-blue-700 hover:underline" data-testid="sp-hero-share-story">
                Share a Story
              </a>
              <span className="text-slate-400">·</span>
              <a href="/help" className="text-blue-700 hover:underline" data-testid="sp-hero-offer-help">
                Offer Help
              </a>
              <span className="text-slate-400">·</span>
              <a href="/submit-cause" className="text-blue-700 hover:underline" data-testid="sp-hero-submit-cause">
                Submit a Cause
              </a>
            </div>
          </div>

          <div className="relative">
            <HeroCollage />
            {/* Handwritten accent (bottom-right of hero collage) */}
            <div
              className="hidden xl:block absolute -right-2 bottom-4 rotate-[6deg] pointer-events-none select-none"
              data-testid="sp-handwrite-stronger"
            >
              <p className="font-serif italic text-emerald-700 text-[15px] leading-tight text-right">
                <span className="block">Stronger</span>
                <span className="block">Communities</span>
                <span className="block">Brighter</span>
                <span className="block">Tomorrows</span>
              </p>
              <svg width="110" height="14" className="ml-auto mr-4 -mt-1">
                <path d="M4 8 Q 40 2, 106 8" stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── Pulse tabs ────────── */}
      <PulseTabs />

      {/* ────────── Your Local Pulse ────────── */}
      <LocalPulseGrid />

      {/* ────────── Network row ────────── */}
      <NetworkRow />

      <SocialPulseFooter />
    </div>
  );
}
